import React, { useState } from 'react';
import { Link, generatePath, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

export const NO_REVEIVER_ASSIGNED = 'No Receiver Assigned';

const containerSx = {
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  paddingLeft: 2,
  paddingRight: 2,
};

const SDToDoNoReceiverAssignedBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="No Receiver Assigned" />
    </Breadcrumbs>
  </Box>
);

const ToDoNoReceiverAssigned = () => {
  const history = useHistory();
  const location = useLocation();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);
  const [loading, setLoading] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const unassignedRescues = (sdTodos?.unassignedRescues || []).map((rescue) => ({
    ...rescue,
    details: { ...rescue.details, id: rescue.details?.donation_id },
  }));

  const handleReviewClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() =>
      dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false))
    )
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
          history.push(generatePath(routes.donation_edit, { donationId: todo.details.id }), { toDoCardIndex: currentCardIndex });
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (rescue, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() =>
      dispatch(removeSiteTodo(activeSite.id, rescue.entity_id, rescue.entity_type, rescue.type, false))
    )
      .then(() => {
        handleCardOut();
        // Wait for animation to complete before removing todo from the store
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, rescue.entity_id, rescue.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  // Show if there is no more pickups to review or when the last card has been skipped
  if (!unassignedRescues.length || currentCardIndex + 1 > unassignedRescues.length) {
    return (
      <>
        <SDToDoNoReceiverAssignedBreadcrumbs />
        <Box sx={containerSx}>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              WebkitTransform: 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You’re all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <SDToDoNoReceiverAssignedBreadcrumbs />

      <Box sx={containerSx}>
        <Box sx={{ position: 'relative', height: '100%' }}>
          {unassignedRescues.map((donation, index) => (
            <ToDoAlertCard
              key={donation.entity_id}
              rescue={donation}
              type={NO_REVEIVER_ASSIGNED}
              current={index === currentCardIndex}
              next={unassignedRescues.length <= index ? null : index === currentCardIndex + 1}
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleReviewClick}
              content={
                <span>
                  Pickup from{' '}
                  <b>
                    {donation?.details?.location?.length <= 20 ? donation?.details?.location : `${donation?.details?.location?.substr(0, 20)}...`}
                  </b>{' '}
                  from <b>{moment(donation?.details?.pickup_begin, 'HH:mm:ss').format('hh:mma')}</b> to{' '}
                  <b>{moment(donation?.details.pickup_end, 'HH:mm:ss').format('hh:mma')}</b>, has no receiver assigned. Please
                  assign now!
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoNoReceiverAssigned;
